import React, { useState, useEffect, useRef } from 'react';
import './ProgressBar.css';

const formatTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
        return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
        return `${minutes}m ${seconds % 60}s`;
    } else {
        return `${seconds}s`;
    }
};

const ProgressBar = ({ 
  isVisible, 
  progress = 0, 
  currentStore = '', 
  totalStores = 0, 
  isPaused = false, 
  isStopped = false,
  processingState = 'idle'
}) => {
  const [timeElapsed, setTimeElapsed] = useState(0);
  const startTimeRef = useRef(Date.now());
  const intervalRef = useRef(null);
  const pauseStartTimeRef = useRef(null);
  const totalPausedTimeRef = useRef(0);
  const prevIsVisibleRef = useRef(isVisible);
  const prevIsStoppedRef = useRef(isStopped);

  useEffect(() => {
    // Reset timer when starting a new search or transitioning from stopped state
    if ((isVisible && !prevIsVisibleRef.current) || (prevIsStoppedRef.current && !isStopped)) {
      console.log('Resetting timer:', { isVisible, prevIsVisible: prevIsVisibleRef.current, isStopped, prevIsStopped: prevIsStoppedRef.current });
      startTimeRef.current = Date.now();
      totalPausedTimeRef.current = 0;
      pauseStartTimeRef.current = null;
      setTimeElapsed(0);
    }
    prevIsVisibleRef.current = isVisible;
    prevIsStoppedRef.current = isStopped;

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Don't start timer if not visible
    if (!isVisible) {
      return;
    }

    // Handle pause state
    if (isPaused) {
      if (!pauseStartTimeRef.current) {
        pauseStartTimeRef.current = Date.now();
      }
      return;
    } else {
      if (pauseStartTimeRef.current) {
        totalPausedTimeRef.current += Date.now() - pauseStartTimeRef.current;
        pauseStartTimeRef.current = null;
      }
    }

    // Start new interval if not stopped or in finishing state
    if (!isStopped || processingState === 'finishing') {
      intervalRef.current = setInterval(() => {
        const now = Date.now();
        setTimeElapsed(now - startTimeRef.current - totalPausedTimeRef.current);
      }, 1000);
    }

    // Cleanup interval on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isVisible, isStopped, isPaused, processingState]);

  if (!isVisible) return null;

  const progressText = `${Math.round(progress)}%`;
  const processedStores = Math.floor((progress / 100) * totalStores);

  // Show completed state only if stopped and not in finishing state
  if (isStopped && processingState !== 'finishing') {
    return (
      <div className="progress-container">
        <div className="progress-info">
          <div className="progress-status">
            <span className="status-text">
              <i className="fas fa-check-circle"></i> Completed
            </span>
            <span className="time-elapsed">
              Time elapsed: {formatTime(timeElapsed)}
            </span>
          </div>
          <div className="progress-text">100%</div>
        </div>
        <div className="progress-bar">
          <div 
            className="progress-fill completed"
            style={{ width: '100%' }}
          />
        </div>
        <div className="store-info">
          <div className="current-store">
            Processing complete
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="progress-container">
      <div className="progress-info">
        <div className="progress-status">
          <span className={`status-text ${isPaused ? 'paused' : ''} ${processingState === 'finishing' ? 'finishing' : ''}`}>
            {isPaused ? (
              processingState === 'processing' ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Pausing...
                </>
              ) : (
                <>
                  <i className="fas fa-pause-circle"></i> Paused
                </>
              )
            ) : processingState === 'finishing' ? (
              <>
                <i className="fas fa-hourglass-end"></i> Finishing up...
              </>
            ) : (
              <>
                <i className="fas fa-spinner fa-spin"></i> Processing
              </>
            )}
          </span>
          <span className="time-elapsed">
            Time elapsed: {formatTime(timeElapsed)}
          </span>
        </div>
        <div className="progress-text">
          {processingState === 'finishing' ? 
            `${progressText} of current batch` : 
            progressText
          }
        </div>
      </div>
      <div className="progress-bar">
        <div 
          className={`progress-fill ${isPaused ? 'paused' : ''} ${processingState === 'finishing' ? 'finishing' : ''}`}
          style={{ width: `${progress}%` }}
        />
      </div>
      <div className="store-info">
        <div className="current-store" title={currentStore}>
          {processingState === 'finishing' ? 
            currentStore || 'Processing current batch' : 
            currentStore || 'Preparing...'
          }
        </div>
        <div className="store-count">
          {processingState === 'finishing' ? 
            `Completing current batch` :
            `Stores: ${processedStores} / ${totalStores}`
          }
        </div>
      </div>
    </div>
  );
};

export default ProgressBar; 